import React from 'react';
import {
  Box,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  Chip,
  IconButton,
  Collapse,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { get } from '../utils/api';

const PER_PAGE = 50;

const DATE_PRESETS = [
  { k: 'last30', t: 'Last 30 days' },
  { k: '3m', t: 'Last 3 months' },
  { k: '6m', t: 'Last 6 months' },
  { k: '1y', t: 'Last 1 year' },
  { k: 'custom', t: 'Custom range' },
];

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Resolve a preset to { from, to } (YYYY-MM-DD). 'custom' returns nulls so the custom inputs drive it.
function presetRange(preset) {
  const now = new Date();
  const to = ymd(now);
  const s = new Date(now);
  if (preset === 'last30') s.setDate(s.getDate() - 30);
  else if (preset === '3m') s.setMonth(s.getMonth() - 3);
  else if (preset === '6m') s.setMonth(s.getMonth() - 6);
  else if (preset === '1y') s.setFullYear(s.getFullYear() - 1);
  else return { from: '', to: '' };
  return { from: ymd(s), to };
}

const prettyType = (t) => String(t || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function SystemLogPage() {
  const [preset, setPreset] = React.useState('last30');
  const [customFrom, setCustomFrom] = React.useState('');
  const [customTo, setCustomTo] = React.useState('');
  const [type, setType] = React.useState('');
  const [types, setTypes] = React.useState([]);
  const [qInput, setQInput] = React.useState('');
  const [q, setQ] = React.useState('');
  const [page, setPage] = React.useState(1);

  const [logs, setLogs] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [expanded, setExpanded] = React.useState(() => new Set());

  // Load the type dropdown once.
  React.useEffect(() => {
    get('/api/manage/system-logs/types')
      .then((r) => setTypes(Array.isArray(r?.types) ? r.types : []))
      .catch(() => setTypes([]));
  }, []);

  const fetchLogs = React.useCallback(async () => {
    setLoading(true); setError('');
    try {
      const range = preset === 'custom' ? { from: customFrom, to: customTo } : presetRange(preset);
      const params = { page, per_page: PER_PAGE };
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      if (type) params.type = type;
      if (q) params.q = q;
      const res = await get('/api/manage/system-logs', params);
      setLogs(Array.isArray(res?.logs) ? res.logs : []);
      setTotal(Number(res?.total) || 0);
    } catch (e) {
      setError(e?.message || 'Failed to load logs');
      setLogs([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [preset, customFrom, customTo, type, q, page]);

  React.useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Any filter change resets to page 1 (except the page control itself).
  const onFilterChange = (setter) => (val) => { setter(val); setPage(1); };
  const applySearch = () => { setQ(qInput.trim()); setPage(1); };

  const toggleRow = (id) => {
    setExpanded((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const pageCount = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>System Log</Typography>
        <Tooltip title="Refresh">
          <span><IconButton onClick={fetchLogs} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Audit of important system actions (e.g. Woo orders created from Zelle reconciliation).
      </Typography>

      {/* Filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Date range</InputLabel>
          <Select label="Date range" value={preset} onChange={(e) => onFilterChange(setPreset)(e.target.value)}>
            {DATE_PRESETS.map((p) => <MenuItem key={p.k} value={p.k}>{p.t}</MenuItem>)}
          </Select>
        </FormControl>

        {preset === 'custom' && (
          <Stack direction="row" spacing={1}>
            <TextField type="date" label="From" size="small" InputLabelProps={{ shrink: true }} value={customFrom} onChange={(e) => onFilterChange(setCustomFrom)(e.target.value)} />
            <TextField type="date" label="To" size="small" InputLabelProps={{ shrink: true }} value={customTo} onChange={(e) => onFilterChange(setCustomTo)(e.target.value)} />
          </Stack>
        )}

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Log type</InputLabel>
          <Select label="Log type" value={type} onChange={(e) => onFilterChange(setType)(e.target.value)}>
            <MenuItem value="">All types</MenuItem>
            {types.map((t) => <MenuItem key={t} value={t}>{prettyType(t)}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search details…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
          sx={{ minWidth: 220, flex: 1 }}
          InputProps={{ endAdornment: (
            <IconButton size="small" onClick={applySearch}><SearchIcon fontSize="small" /></IconButton>
          ) }}
        />
      </Stack>

      {error && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>}

      <Typography variant="caption" color="text.secondary">
        {loading ? 'Loading…' : `${total} log ${total === 1 ? 'entry' : 'entries'}`}
      </Typography>

      <Box sx={{ overflowX: 'auto', mt: 0.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>When</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>By</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.length === 0 && !loading ? (
              <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No log entries for these filters.</Typography></TableCell></TableRow>
            ) : logs.map((log) => {
              const open = expanded.has(log.id);
              return (
                <React.Fragment key={log.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleRow(log.id)}>
                        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.created_at}</TableCell>
                    <TableCell><Chip size="small" label={prettyType(log.action_type)} /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{log.action_by}</TableCell>
                    <TableCell>{log.summary || '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box
                          component="pre"
                          sx={{ m: 1, p: 1.5, bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider', borderRadius: 1, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                          {JSON.stringify(log.details ?? {}, null, 2)}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {pageCount > 1 && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Pagination count={pageCount} page={page} onChange={(_, val) => setPage(val)} color="primary" size="small" />
        </Stack>
      )}

      {loading && logs.length === 0 && (
        <Stack direction="row" justifyContent="center" sx={{ py: 3 }}><CircularProgress size={24} /></Stack>
      )}
    </Box>
  );
}
